import { Component, input, OnChanges } from '@angular/core';

import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { SemantriaConfiguration } from 'app/providers/semantria-api/configuration.service';
import { ProjectService } from 'app/providers/storage-api/project.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

interface ConfigRouteTableData {
  config_route_id: string;
  config_route_name: string;
}

interface LayerChildConfig {
  [key: string]: SemantriaConfiguration[];
}

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-activity-table',
  templateUrl: './configuration-activity-table.component.html',
  styleUrls: ['./configuration-activity-table.component.scss'],
  standalone: false,
})
export class ConfigurationActivityTableComponent implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly configuration = input<SemantriaConfiguration>();
  readonly configurations = input<SemantriaConfiguration[]>();
  readonly autoAnalysisConfigs = input<string[]>();
  readonly projectsCollections = input<any[]>([]);

  autoAnalysisTableColumns = ['name', 'project', 'collection', 'docs'];
  autoAnalysisTableData: MatTableDataSource<{
    projectName;
    collectionName;
    documentCount;
    analysisName;
  }>;
  layeredConfigTableColumns = ['name'];
  layeredConfigTableData: MatTableDataSource<{ layeredConfigName }>;
  configRouteTableColumns = ['name'];
  configRouteTableData: MatTableDataSource<ConfigRouteTableData>;

  isLoading: boolean;
  private sort: Sort;

  constructor(private projectService: ProjectService) {
    this.layeredConfigTableData = new MatTableDataSource([]);
    this.autoAnalysisTableData = new MatTableDataSource([]);
    this.configRouteTableData = new MatTableDataSource([]);
  }

  ngOnChanges() {
    this.autoAnalysisTableData.data = [];
    this.layeredConfigTableData.data = [];
    this.configRouteTableData.data = [];

    if (this.configuration() && this.projectsCollections()) {
      if (this.autoAnalysisConfigs()) {
        this.createAutoAnalysisTable();
      }
      if (this.configurations()) {
        this.createLayeredConfigTable();
      }

      this.createConfigRouteTable();
    }
  }

  private async createAutoAnalysisTable() {
    const autoAnalysisTableDataArray = [];
    if (this.autoAnalysisConfigs().includes(this.configuration().id)) {
      this.isLoading = true;
      const filteredProjectCollections = this.projectsCollections().filter(
        (col) => col.isAutoAnalyze === true && col.configId === this.configuration().id
      );
      const promises = [];
      for (const col of filteredProjectCollections) {
        promises.push(this.projectService.getProjectById(col.projectId));
      }
      Promise.all(promises)
        .then((res) => {
          const projAnalyses = res
            .map((proj) => proj.analyses.filter((p) => p.configId === this.configuration().id))
            .reduce((acc, curr) => {
              for (const c of curr) {
                if (!acc.some((item) => item.name === c.name)) {
                  acc.push(c);
                }
              }
              return acc;
            }, []);

          for (const analysis of projAnalyses) {
            for (const coll of filteredProjectCollections) {
              if (coll.analysisId === analysis.name) {
                autoAnalysisTableDataArray.push({
                  projectName: coll.projectName,
                  collectionName: coll.displayName,
                  documentCount: coll.documentCount,
                  analysisName: analysis.displayName,
                });
              }
            }
          }

          this.autoAnalysisTableData.data = autoAnalysisTableDataArray;
        })
        .finally(() => (this.isLoading = false));
    }
  }

  private createLayeredConfigTable() {
    const layeredConfigs = this.configurations().filter(
      (c: SemantriaConfiguration) => c.layer && !c.removed
    );
    const childMap: LayerChildConfig[] = [];

    for (const lc of layeredConfigs) {
      for (const cId of lc.parents) {
        if (!childMap[cId]) {
          childMap[cId] = [];
        }
        childMap[cId].push(lc);
      }
    }

    const layeredConfigTableDataArray = [];

    if (childMap[this.configuration().id]) {
      for (const parent of childMap[this.configuration().id]) {
        layeredConfigTableDataArray.push({
          layeredConfigName: parent.name,
        });
      }
    }

    this.layeredConfigTableData.data = layeredConfigTableDataArray;
  }

  private createConfigRouteTable() {
    let configRouteTableDataArray = [];

    if (this.configuration().config_routes) {
      configRouteTableDataArray = this.configuration().config_routes;
    }

    this.configRouteTableData.data = configRouteTableDataArray;
  }

  private propSort(a, b, dir) {
    if (typeof a === 'string') {
      return a.localeCompare(b) * dir;
    } else {
      return (a - b) * dir;
    }
  }

  private setSortData($event) {
    let direction = 1;
    let active = 'name';
    this.sort = $event;

    if (this.sort?.direction) {
      direction = this.sort.direction === 'asc' ? 1 : -1;
    }
    if (this.sort?.active) {
      active = this.sort.active;
    }

    return {
      direction,
      active,
    };
  }

  public onAutoAnalysisTableSortChanged($event = this.sort) {
    const sortData = this.setSortData($event);

    this.autoAnalysisTableData.data.sort((u1, u2) => {
      switch (sortData.active) {
        case 'name':
          return this.propSort(u1.analysisName, u2.analysisName, sortData.direction);
        case 'project':
          return this.propSort(u1.projectName, u2.projectName, sortData.direction);
        case 'collection':
          return this.propSort(u1.collectionName, u2.collectionName, sortData.direction);
        case 'docs':
          return this.propSort(u1.documentCount, u2.documentCount, sortData.direction);
        default:
          return 0;
      }
    });

    this.autoAnalysisTableData.data = [...this.autoAnalysisTableData.data];
  }

  public onLayeredConfigTableSortChanged($event = this.sort) {
    const sortData = this.setSortData($event);

    this.layeredConfigTableData.data.sort((u1, u2) => {
      switch (sortData.active) {
        case 'name':
          return this.propSort(u1.layeredConfigName, u2.layeredConfigName, sortData.direction);
        default:
          return 0;
      }
    });

    this.layeredConfigTableData.data = [...this.layeredConfigTableData.data];
  }

  public onConfigRouteTableSortChanged($event = this.sort) {
    const sortData = this.setSortData($event);

    this.configRouteTableData.data.sort((u1, u2) => {
      switch (sortData.active) {
        case 'name':
          return this.propSort(u1.config_route_name, u2.config_route_name, sortData.direction);
        default:
          return 0;
      }
    });

    this.configRouteTableData.data = [...this.configRouteTableData.data];
  }
}
